import type { ReactNode } from 'react';
import { Alert, type AlertSeverity } from '@tempmee/react';
import { pluralize } from '~/utils/String';
import { IShiftAlertContext, resolveShiftAlert, TShiftAlertKind } from '../utils/shiftAlerts';

export interface IShiftAlertSlotProps extends IShiftAlertContext {
  /**
   * The Counter Adjustment panel, rendered whenever the candidate countered. It
   * lives here rather than beside this component so that one place decides what
   * occupies the slot — the counter's suppression of "Confirm now" would
   * otherwise depend on two call sites agreeing.
   */
  counterOfferAlert?: ReactNode;
}

/**
 * The Confirm Candidates pages' alert slot: at most one banner, then the counter
 * offer's terms when the candidate countered.
 *
 * {@link resolveShiftAlert} picks the banner. The counter panel is rendered on its
 * own condition rather than ranked against the banners, because an office whose
 * card has expired still needs to read the terms it will be confirming once the
 * card is fixed.
 *
 * None of it is clickable. The legacy screen wired three of these banners to
 * something: the payment banner opened an add-payment popup, and both charge
 * banners were collapsible buttons hiding the very sentence the office needed.
 * Here the copy is simply shown in full, so there is nothing left to click and no
 * way to miss it.
 */
export const ShiftAlertSlot = ({ counterOfferAlert, ...context }: IShiftAlertSlotProps) => {
  const kind = resolveShiftAlert(context);
  const counterTerms = context.hasCounterOffer ? counterOfferAlert : null;

  if (kind === null) {
    return <>{counterTerms}</>;
  }

  const { severity, title, description } = ALERT_CONTENT[kind]({
    competingOffersCount: context.competingOffersCount ?? 0,
  });

  return (
    <>
      <Alert
        severity={severity}
        title={title}
        description={description}
        data-testid={`shift-alert-${kind}`}
        data-cy={`shift-alert-${kind}`}
      />
      {counterTerms}
    </>
  );
};

interface IAlertContentArgs {
  competingOffersCount: number;
}

type TAlertContent = {
  severity: AlertSeverity;
  /** The bold first line. Only the CTA alert has one. */
  title?: ReactNode;
  description: ReactNode;
};

/**
 * Copy per banner kind, carried over from the banners the redesign replaces so
 * the wording an office already recognises does not change with the layout.
 */
const ALERT_CONTENT: Record<TShiftAlertKind, (args: IAlertContentArgs) => TAlertContent> = {
  'payment-invalid': () => ({
    severity: 'error',
    description:
      'Your payment method is either missing or expired. Please update it to unlock candidate names and send invites to professionals.',
  }),

  // `cta` is the design's severity here — the lightbulb, not the info circle:
  // this is a nudge to act, not a system state. It is the one alert on the page
  // that carries a title, which is why `title` is optional above.
  'competing-offers': ({ competingOffersCount }) => ({
    severity: 'cta',
    title: 'Confirm now to secure coverage',
    description: `Professional has applied to ${competingOffersCount} other ${pluralize(
      'shift',
      competingOffersCount,
    )} the same day.`,
  }),
};
